import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
} from "@mui/material";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import IMAGES from "../../../../../../assets/images/images";
import ConfirmDeleteMapModal from "./ConfirmDeleteMapModal";
import { BrickManagementStore } from "../../../../../../store/main/BrickManagementStore";
import { TerritoryDataStore } from "../../../../../../store/main/TerritoryDataStore";
import { CustomerDatabaseStore } from "../../../../../../store/main/CustomerDatabaseStore";
import { FetchState } from "../../../../../../domain/models/fetch-state";
import ConfirmAddAreaModal from "./ConfirmAddMapModal";
import Swal from "sweetalert2";
import { BricksType } from "../../../../../../domain/models/brick-management-report/brick-management-data";
import { Map } from "../../../../../../common/constants/constant";
import ExceededAreaModal from "./ConfirmAddedAreasLimit";
import MapName from "../../../../../../common/enums/Config";

interface Payload {
  hq_headquarter_id: string;
  no_of_work_days: number | string;
  name: string;
  expected_business: number | string;
  distance_from_hq: string | number;
  station: string;
  distance_from_base_brick: number | string;
  base_brick_id?: string | null;
  district: string;
  clusters: (string | number)[];
}

interface LocationMetadata {
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  place_id: string;
}

interface BrickLocation {
  location_metadata: LocationMetadata;
}

interface FetchBrickAreaLocationResponse {
  brick_locations: BrickLocation[];
}

interface Props {
  open: boolean;
  handleClose: () => void;
  successCallback: () => void;
  brick: BricksType;
}

export type FetchAutoCompleteLocationType = {
  success: boolean;
  location_data: LocationDataType;
};

export type LocationDataType = {
  predictions: PredictionType[];
};

export type PredictionType = {
  description: string;
  place_id: string;
};

const UpdateBrickMapViewModal: React.FC<Props> = ({
  open,
  handleClose,
  successCallback,
  //   hq_id,
  brick,
  //   payload,
}) => {
  const {
    FetchAutoCompleteLocation,
    fetchAutoCompleteLocation,
    FetchAutoCompleteLocationState,
    FetchMapLocation,
    FetchMapLocationState,
    fetchMapLocation,
    addBrick,
    fetchBrickAreaLocation,
    FetchBrickAreaLocationState,
    FetchBrickAreaLocation,
    addStoreBrickAreaLocation,
    AddStoreBrickAreaLocationState,
    fetchBrickManagement,
    fetchMapDistance,
    FetchMapDistanceState,
    fetchMaxLocationLimit,
    FetchMaxLocationLimitState,
  } = BrickManagementStore();

  const { decodeToken, userDetails } = CustomerDatabaseStore();

  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<PredictionType[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [addedAreas, setAddedAreas] = useState<
    { formatted_address: string; place_id: string; lat: number; lng: number }[]
  >([]);

  const [markers, setMarkers] = useState<
    { lat: number; lng: number; color: string; place_id: string }[]
  >([]);
  const [center, setCenter] = useState<
    { lat: number; lng: number } | undefined
  >({ lat: 21.7679, lng: 78.8718 });
  const [zoom, setZoom] = useState<number>(4);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [areaToDelete, setAreaToDelete] = useState<{
    formatted_address: string;
    place_id: string;
  } | null>(null);
  const [mapKey, setMapKey] = useState<number>(0);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [areaToAdd, setAreaToAdd] = useState<{
    formatted_address: string;
    place_id: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [array, setArray] = useState<any[]>([]);
  const [brickLocationsAvailable, setBrickLocationsAvailable] = useState(false);
  const [exceededAreaModalOpen, setExceededAreaModalOpen] =
    useState<boolean>(false);
  const [apiCallLoading, setApiCallLoading] = useState<boolean>(false);

  const userOrgId = userDetails.organization_id;
  const brickAreaLocations = MapName.MAX_LOCATION_LIMIT;

  useEffect(() => {
    if (brick.brick_id) {
      fetchData();
    }
  }, [brick.brick_id]);

  const fetchData = async () => {
    try {
      const result = await fetchBrickAreaLocation({
        brick_id: brick.brick_id,
      });

      if (result.success === true) {
        if (result && result.brick_locations) {
          setBrickLocationsAvailable(true);

          const newMarkers = result.brick_locations.map(
            (location: BrickLocation) => ({
              lat: location.location_metadata.geometry.location.lat,
              lng: location.location_metadata.geometry.location.lng,
              color: "red",
              place_id: location.location_metadata.place_id,
            })
          );
          setMarkers(newMarkers);
          if (newMarkers.length > 0) {
            setCenter({
              lat: newMarkers[0].lat,
              lng: newMarkers[0].lng,
            });
            setZoom(12);
          }

          const existingAreas = result.brick_locations.map(
            (location: BrickLocation) => ({
              lat: location.location_metadata.geometry.location.lat,
              lng: location.location_metadata.geometry.location.lng,
              formatted_address: location.location_metadata.formatted_address,
              place_id: location.location_metadata.place_id,
            })
          );

          setAddedAreas(existingAreas);

          const metadataList = result.brick_locations.map(
            (item: any) => item.location_metadata
          );

          setArray(metadataList);

          setMapKey((prevKey) => prevKey + 1);
        } else {
          setBrickLocationsAvailable(false);
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (markers.length > 0 && addedAreas.length > 0) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [markers, addedAreas]);

  const handleInputChange = (
    event: React.ChangeEvent<{}>,
    newValue: string
  ) => {
    setInputValue(newValue);
    if (newValue.trim()) {
      fetchAutoCompleteLocation({ input: newValue });
    }
  };

  const handleChange = async (
    event: React.SyntheticEvent<Element, Event>,
    value: string | PredictionType | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<PredictionType>
  ) => {
    if (value && typeof value !== "string") {
      const selectedOption = options.find(
        (option) => option.place_id === value.place_id
      );
      if (selectedOption) {
        let result = await fetchMapLocation({
          place_id: selectedOption.place_id,
        });
        if (result.success === true) {
          const markerExists = markers.some(
            (item) => item.place_id === result.location_data.results[0].place_id
          );
          setArray([...array, result.location_data.results[0]]);

          const areaExists = addedAreas.some(
            (area) => area.place_id === result.location_data.results[0].place_id
          );
          setArray([...array, result.location_data.results[0]]);

          if (!markerExists && !areaExists) {
            let location = result.location_data.results[0].geometry.location;
            setCenter(location);
            setZoom(12);

            let newMarker = {
              lat: location.lat,
              lng: location.lng,
              color: "red",
              place_id: result.location_data.results[0].place_id,
            };
            setMarkers([...markers, newMarker]);

            const newArea = {
              formatted_address:
                result.location_data.results[0].formatted_address,
              place_id: result.location_data.results[0].place_id,
              lat: location.lat,
              lng: location.lng,
            };

            setArray([...array, result.location_data.results[0]]);
            if (addedAreas.length === 0) {
              setMarkers((prevMarkers) => [
                ...prevMarkers,
                {
                  lat: location.lat,
                  lng: location.lng,
                  color: "red",
                  place_id: result.location_data.results[0].place_id,
                },
              ]);
              setAreaToAdd(newArea);

              setIsConfirmModalOpen(true);
            } else {
              const origins = addedAreas.map((area) => area.formatted_address);
              const destination = newArea.formatted_address;

              const distanceResult = await fetchMapDistance({
                origins: origins,
                destinations: [destination],
              });

              let isWithinLimit = true;

              if (distanceResult.success) {
                const distanceElements = distanceResult.distance_data.rows.map(
                  (row: any) => row.elements[0]
                );

                for (const element of distanceElements) {
                  const distanceValue = element.distance.value;

                  const maxDistanceResponse = await fetchMaxLocationLimit({
                    organization_id: userOrgId,
                    name: brickAreaLocations,
                  });

                  if (maxDistanceResponse.success) {
                    const maxLocationDistance =
                      maxDistanceResponse.config[0].configuration
                        .max_location_distance;

                    if (distanceValue > maxLocationDistance * 1000) {
                      isWithinLimit = false;
                      break;
                    }
                  }
                }
              }

              if (isWithinLimit) {
                setMarkers((prevMarkers) => [
                  ...prevMarkers,
                  {
                    lat: location.lat,
                    lng: location.lng,
                    color: "red",
                    place_id: result.location_data.results[0].place_id,
                  },
                ]);
                setAreaToAdd(newArea);
                setIsConfirmModalOpen(true);
              } else {
                setExceededAreaModalOpen(true);
                setMarkers((prevMarkers) =>
                  prevMarkers.filter(
                    (marker) =>
                      marker.place_id !==
                      result.location_data.results[0].place_id
                  )
                );
              }
            }
          }
        }
      }
    }
  };

  

  const handleMapClick = async (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
    
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      setMarkers((prevMarkers) =>
        prevMarkers.filter((marker) =>

          addedAreas.some((area) => area.place_id === marker.place_id)
        )
      );
      const result = await fetchMapLocation({ lat, lng });

      if (result.success) {
        const placeId = result.location_data.results[0].place_id;
        const markerExists = markers.some((item) => item.place_id === placeId);
        const areaExists = addedAreas.some((area) => area.place_id === placeId);

        setArray((prevArray) => [
          ...prevArray,
          result.location_data.results[0],
        ]);

        if (!markerExists && !areaExists) {
        
          const location = result.location_data.results[0].geometry.location;
          setCenter(location);
          setZoom(12);

      
          const newMarker = {
            lat: location.lat,
            lng: location.lng,
            color: "red",
            place_id: placeId,
          };

          const newArea = {
            formatted_address:
              result.location_data.results[0].formatted_address,
            place_id: placeId,
            lat: location.lat,
            lng: location.lng,
          };

    
          if (addedAreas.length === 0) {
            setMarkers([newMarker]);
            setAreaToAdd(newArea);
            setIsConfirmModalOpen(true);
          } else {
            const origins = addedAreas.map((area) => area.formatted_address);
            const destination = newArea.formatted_address;
            const distanceResult = await fetchMapDistance({
              origins,
              destinations: [destination],
            });

            let isWithinLimit = true;
            if (distanceResult.success) {
              const distanceElements = distanceResult.distance_data.rows.map(
                (row: any) => row.elements[0]
              );

              for (const element of distanceElements) {
                const distanceValue = element.distance.value;

                const maxDistanceResponse = await fetchMaxLocationLimit({
                  organization_id: userOrgId,
                  name: brickAreaLocations,
                });

                if (maxDistanceResponse.success) {
                  const maxLocationDistance =
                    maxDistanceResponse.config[0].configuration
                      .max_location_distance;

                  if (distanceValue > maxLocationDistance * 1000) {
                    isWithinLimit = false;
                    break;
                  }
                }
              }
            }

            if (isWithinLimit) {
              setMarkers((prevMarkers) => {
                const confirmedMarkers = prevMarkers.filter((marker) =>
                  addedAreas.some((area) => area.place_id === marker.place_id)
                );
                return [...confirmedMarkers, newMarker];
              });
              setAreaToAdd(newArea);
              setIsConfirmModalOpen(true);
            } else {
              setExceededAreaModalOpen(true);
              setMarkers((prevMarkers) =>
                prevMarkers.filter((marker) => marker.place_id !== placeId)
              );
            }
          }
        }
      }
    }
  };

  const handleCloseExceededAreaModal = () => {
    setExceededAreaModalOpen(false);
  };

  useEffect(() => {}, [markers]);

  const confirmAddArea = () => {
    if (areaToAdd) {
      addArea(areaToAdd);
      setIsConfirmModalOpen(false);
      setIsExpanded(true);
      setInputValue("");
    }
  };

  const handleCancelAddArea = () => {
    setMarkers((prevMarkers) => prevMarkers.slice(0, -1));
    setIsConfirmModalOpen(false);
  };

  const addArea = (newArea: {
    formatted_address: string;
    place_id: string;
    lat: number;
    lng: number;
  }) => {
    const location =
      FetchMapLocation.location_data.results[0].geometry.location;

    setAddedAreas((prevAreas) => [...prevAreas, newArea]);
  };

  const handleDeleteArea = (areaToDelete: {
    formatted_address: string;
    place_id: string;
  }) => {
    setAddedAreas(
      addedAreas.filter((item) => item.place_id !== areaToDelete.place_id)
    );

    setMarkers(
      markers.filter((item) => item.place_id !== areaToDelete.place_id)
    );
    setMapKey((prevKey) => prevKey + 1);
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    if (FetchAutoCompleteLocationState === FetchState.SUCCESS) {
      if (
        FetchAutoCompleteLocation &&
        FetchAutoCompleteLocation.location_data
      ) {
        const fetchedOptions =
          FetchAutoCompleteLocation.location_data.predictions.map(
            (prediction) => ({
              description: prediction.description,
              place_id: prediction.place_id,
            })
          );
        setOptions(fetchedOptions);
      }
    }
  }, [FetchAutoCompleteLocation, FetchAutoCompleteLocationState]);

  const handleUpdateBrick = async () => {
    if (apiCallLoading) return; 
    setApiCallLoading(true);

    let params = {
      locations: addedAreas.map((area) => ({
        brick_id: brick.brick_id,
        lattitude: area.lat.toFixed(7),
        longitude: area.lng.toFixed(7),
        location_metadata: array.find(
          (item) => item.place_id === area.place_id
        ),
      })),
    };

    try {
      let result = await addStoreBrickAreaLocation(params);
      if (result.success) {
        fetchBrickManagement({ page: 1 });
        successCallback();
        Swal.fire({
          icon: "success",
          timer: 3000,
          text: result.message,
          customClass: {
            container: "swal2-style",
          },
          confirmButtonColor: "#3085d6",
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: "Something went wrong. Please try again.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setApiCallLoading(false); // Ensure loading is reset
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        position={"absolute"}
        top={"50%"}
        left={"50%"}
        bgcolor={"background.paper"}
        borderRadius={"22px"}
        sx={{
          transform: "translate(-50%, -50%)",
          display: "flex",
          overflow: "hidden",
        }}
        width="100%"
        p={3}
      >
        <Box display="flex" flexDirection="row" width="100%" height="100%">
          <Box flex="1">
            <Box
              display="flex"
              position={"absolute"}
              top={20}
              zIndex={1}
              bgcolor={"white"}
              marginTop={8}
              marginLeft={2}
              width={"30%"}
            >
              <Box flex="1" marginRight={2}>
                <Typography
                  fontWeight={500}
                  variant="h6"
                  textAlign="left"
                  marginTop={1}
                  marginLeft={3}
                >
                  Search Your Areas
                </Typography>
                <Box position="absolute" top={5} right={16} zIndex="tooltip">
                  <Button
                    onClick={() => handleClose()}
                    sx={{ height: "30px", width: "30px", minWidth: "30px" }}
                  >
                    <CloseIcon fontSize="medium" color="info" />
                  </Button>
                </Box>

                <Autocomplete
                  sx={{ marginBottom: "20px", marginLeft: "20px" }}
                  disablePortal
                  freeSolo
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  options={options}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.description
                  }
                  onChange={handleChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Search Locations on Map"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Box
                  flex="1"
                  pl={2}
                  sx={{ overflowY: "auto", maxHeight: "40vh" }}
                >
                  <Typography
                    fontWeight={700}
                    variant="h6"
                    textAlign="left"
                    marginBottom={2}
                  >
                    Added Areas
                  </Typography>
                  <Typography
                    color={addedAreas.length < 2 ? "error" : "textPrimary"}
                    mt={1}
                    fontWeight={700}
                  >
                    {addedAreas.length < 2
                      ? "Add at least 2 locations to Save"
                      : `${addedAreas.length} Areas added`}
                  </Typography>
                  <Stack spacing={1}>
                    {addedAreas.length > 0 &&
                      addedAreas.map((area, index) => (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography>{area.formatted_address}</Typography>

                          <IconButton
                            onClick={() => {
                              setAreaToDelete(area);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                  </Stack>
                </Box>
                <Box display="flex" justifyContent="center" width="100%" mt={2}>
                  <Button
                    variant="contained"
                    sx={{ width: "20%", marginBottom: "5%" }}
                    disabled={addedAreas.length < 2 || apiCallLoading}
                    onClick={handleUpdateBrick}
                  >
                    {apiCallLoading ? "Saving..." : "Save"}
                  </Button>
                </Box>
                <ConfirmAddAreaModal
                  open={isConfirmModalOpen}
                  onClose={handleCancelAddArea}
                  onConfirm={confirmAddArea}
                  area={areaToAdd}
                />

                <ExceededAreaModal
                  open={exceededAreaModalOpen}
                  onClose={handleCloseExceededAreaModal}
                />
              </Box>
            </Box>
            <LoadScript
              googleMapsApiKey={"AIzaSyCv-70Q4VJ5VZYPzQs06SuUiA-QX0195Nw"}
            >
              <GoogleMap
                key={mapKey}
                mapContainerStyle={{ height: "100vh", width: "100%" }}
                center={center}
                zoom={zoom}
                onClick={handleMapClick}
              >
                {markers.map((marker, index) => (
                  <Marker
                    key={marker.place_id}
                    position={{ lat: marker.lat, lng: marker.lng }}
                    icon={{
                      url: `http://maps.google.com/mapfiles/ms/icons/red-dot.png`,
                      scaledSize: new window.google.maps.Size(30, 30),
                    }}
                  />
                ))}
              </GoogleMap>
            </LoadScript>
          </Box>
        </Box>
        {areaToDelete && (
          <ConfirmDeleteMapModal
            open={isDeleteModalOpen}
            area={areaToDelete}
            onClose={() => setIsDeleteModalOpen(false)}
            onDelete={() => {
              if (areaToDelete) {
                handleDeleteArea(areaToDelete);
              }
            }}
          />
        )}
      </Box>
    </Modal>
  );
};

export default UpdateBrickMapViewModal;
